import { useState, useEffect, useRef } from "react";
import styles from "./NewsSection.module.css";
import Slider from "../Slider/Slider";
import { BASE_URL } from "../../config";

const formatDate = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${day}.${month}.${year}`;
};

const NewsCard = ({ news, isExpanded, onToggle }) => {
  const descRef = useRef(null);
  const cardRef = useRef(null);
  const animationRef = useRef('closed');
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [animationState, setAnimationState] = useState('closed');
  const [needsButton, setNeedsButton] = useState(false); // Сохраняем, нужна ли кнопка

  // Проверяем переполнение контента
  useEffect(() => {
    const el = descRef.current;
    if (!el) return;

    const checkOverflow = () => {
      // Проверяем реальное переполнение для закрытого состояния
      const originalOverflow = el.style.overflow;
      el.style.overflow = 'auto';
      const hasOverflow = el.scrollHeight > el.clientHeight;
      el.style.overflow = originalOverflow;

      setIsOverflowing(hasOverflow);

      // Если контент переполнен, запоминаем что кнопка нужна
      if (hasOverflow) {
        setNeedsButton(true);
      }
    };

    const timer = setTimeout(checkOverflow, 100);
    checkOverflow();

    window.addEventListener("resize", checkOverflow);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", checkOverflow);
    };
  }, [news.short_description]);

  // Управление анимацией при изменении isExpanded
  useEffect(() => {
    const set = (s) => { animationRef.current = s; setAnimationState(s); };
    let timer;
    if (isExpanded) {
      set('opening');
      timer = setTimeout(() => set('open'), 50);
    } else if (animationRef.current === 'open') {
      set('closing');
      timer = setTimeout(() => set('closed'), 300);
    }
    return () => clearTimeout(timer);
  }, [isExpanded]);

  const handleToggle = () => {
    if (animationState === 'opening' || animationState === 'closing') return;
    onToggle();
  };

  const getCardClasses = () => {
    let classes = styles.card;
    if (animationState === 'open') classes += ` ${styles.cardExpanded}`;
    if (animationState === 'opening') classes += ` ${styles.cardOpening}`;
    if (animationState === 'closing') classes += ` ${styles.cardClosing}`;
    return classes;
  };

  const getDescriptionClasses = () => {
    let classes = styles.cardDescription;
    if (animationState === 'open' || animationState === 'opening') classes += ` ${styles.expanded}`;
    return classes;
  };

  const showButton = needsButton || animationState === 'open';

  return (
    <div className={styles.cardWrapper}>
      <div
        ref={cardRef}
        className={getCardClasses()}
      >
        <div className={styles.cardHeader}>
          {news.published_at && <div className={styles.cardDate}>{formatDate(news.published_at)}</div>}
        </div>
        <div className={styles.cardContent}>
          <p className={styles.cardTitle}>{news.title}</p>
          <div
            className={getDescriptionClasses()}
            ref={descRef}
            dangerouslySetInnerHTML={{__html: news.short_description}}
          />
          {showButton && (
            <button className={styles.readMoreBtn} onClick={handleToggle}>
              <span>{animationState === 'open' ? "Close" : "Read more"}</span>
              <svg className={`${styles.readMoreIcon} ${animationState === 'open' ? styles.rotated : ''}`} width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="11" stroke="#2F57BF" strokeWidth="0.58" />
                <path d="M8 10L12 14L16 10" stroke="#2F57BF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const NewsSection = () => {
  const [newsData, setNewsData] = useState([]);
  const [visibleSlides, setVisibleSlides] = useState(3);
  const [expandedCardId, setExpandedCardId] = useState(null);

  // Загрузка новостей
  useEffect(() => {
    const url = new URL(`${BASE_URL}/v1/news/`);
    url.searchParams.append("page", 1);
    url.searchParams.append("page_size", 12);
    fetch(url)
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => setNewsData(data.data || []))
      .catch(() => {});
  }, []);

  // Адаптив слайдера
  useEffect(() => {
    const update = () => {
      if (window.innerWidth <= 500) setVisibleSlides(1);
      else if (window.innerWidth <= 900) setVisibleSlides(2);
      else setVisibleSlides(3);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Закрытие карточки при клике вне её
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (expandedCardId !== null) {
        const isClickOnCard = event.target.closest(`.${styles.card}`);
        const isClickOnButton = event.target.closest(`.${styles.readMoreBtn}`);

        if (!isClickOnCard && !isClickOnButton) {
          setExpandedCardId(null);
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [expandedCardId]);

  const handleToggle = (cardId) => {
    setExpandedCardId(expandedCardId === cardId ? null : cardId);
  };

  if (!newsData.length) return null;

  const items = newsData.map((news) => (
    <NewsCard
      key={news.id}
      news={news}
      isExpanded={expandedCardId === news.id}
      onToggle={() => handleToggle(news.id)}
    />
  ));

  return (
    <section className={`height ${styles.main}`}>
      <h2 className="h2-bold">News</h2>
      <Slider items={items} visibleSlides={visibleSlides} />
    </section>
  );
};

export default NewsSection;